import { SpecificationProp } from 'features/car-details-card/types'
import { createMediaStyles } from 'lib/styled-helpers'
import { usePriceFormatter } from 'lib/use-price-formatter'
import React, { FC } from 'react'
import { useCarDetailsCardConfig } from 'stages/car-details'
import styled, { useTheme } from 'styled-components'
import { Collapse } from 'ui/collapse'
import { DottedPropertyValue } from 'ui/dotted-property-value'
import { Cell, Row } from 'ui/grid'
import { Tab, Tabs } from 'ui/tabs'

const PropertyListComponent = ({
  props,
  remainderValue
}: {
  props: SpecificationProp[]
  remainderValue: 0 | 1 | null
}) => {
  return (
    <PropertyList>
      {props.map((prop, index) => {
        return index % 2 === remainderValue || remainderValue === null ? (
          <PropertyItem key={index}>
            <DottedPropertyValue
              property={<PropertyText>{prop.title}</PropertyText>}
              value={<Value>{prop.definition}</Value>}
            ></DottedPropertyValue>
            {/* <Property>
              <PropertyText>{prop.title}</PropertyText>
            </Property>
            <Value>{prop.definition}</Value> */}
          </PropertyItem>
        ) : null
      })}
    </PropertyList>
  )
}

export type Props = {
  additionalEquipmentCost: number | null
}

export const DetailsCardTabs: FC<Props> = ({ additionalEquipmentCost }) => {
  const { carViewSettings, tabs } = useCarDetailsCardConfig()
  const formatPrice = usePriceFormatter()
  const theme = useTheme()
  const isCompactView = ['phone', 'phablet'].includes(theme.activeBreakpoint)

  return (
    <Tabs tabsLength={tabs.length}>
      {tabs.map(({ title, content }, index: number) => {
        return (
          <Tab title={title} key={index}>
            {content.element === 'options' ? (
              <>
                {content.value.sections.items.map((item, index) => {
                  return (
                    <ItemWrapper key={index}>
                      <Collapse title={item.title}>
                        <OptionList>
                          {item.content.options.map((option, index) => {
                            return <OptionItem key={index}>{option}</OptionItem>
                          })}
                        </OptionList>
                      </Collapse>
                    </ItemWrapper>
                  )
                })}

                {content.value.representsAdditionalEquipment && additionalEquipmentCost && (
                  <EquipmentPrice>
                    <DottedPropertyValue
                      property={carViewSettings.i18n.additionalEquipmentPrice}
                      value={formatPrice(additionalEquipmentCost)}
                    ></DottedPropertyValue>
                  </EquipmentPrice>
                )}
              </>
            ) : content.element === 'specifications' ? (
              content.value.sections.map((section, index) => {
                return (
                  <SpecificationWrapper key={index}>
                    <Title>{section.title}</Title>
                    {isCompactView ? (
                      <PropertyListComponent props={section.props} remainderValue={null} />
                    ) : (
                      <Row>
                        <Cell size={6}>
                          <Row flexDirection="column">
                            <Cell>
                              <PropertyListComponent props={section.props} remainderValue={0} />
                            </Cell>
                          </Row>
                        </Cell>
                        <Cell size={6}>
                          <Row flexDirection="column">
                            <Cell>
                              <PropertyListComponent props={section.props} remainderValue={1} />
                            </Cell>
                          </Row>
                        </Cell>
                      </Row>
                    )}
                  </SpecificationWrapper>
                )
              })
            ) : (
              <div>not implemented</div>
            )}
          </Tab>
        )
      })}
    </Tabs>
  )
}

const ItemWrapper = styled.div`
  border-top: ${({ theme }) => `1px solid ${theme.palette.grey[200]}`};
  border-bottom: ${({ theme }) => `1px solid ${theme.palette.grey[200]}`};
  margin-top: -1px;

  &:first-child {
    margin-top: ${({ theme }) => theme.spacing[4]};
  }
`

const OptionList = styled.ul`
  margin: 0;
  padding-right: ${({ theme }) => theme.spacing[4]};
  padding-left: ${({ theme }) => theme.spacing[4]};
  padding-bottom: ${({ theme }) => theme.spacing[3]};
`

const OptionItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;
  color: ${({ theme }) => theme.palette.grey[500]};
  ${({ theme }) => theme.textStyles.p2};

  &:before {
    content: '';
    display: block;
    flex-shrink: 0;
    width: 7px;
    height: 7px;
    margin-top: 6px;
    margin-right: 12px;
    margin-left: 3px;
    border-radius: 50%;
    background: ${({ theme }) => theme.palette.brand.primary};
  }
`

const Title = styled.div`
  margin-top: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  ${({ theme }) => theme.textStyles.p1};
  color: ${({ theme }) => theme.palette.grey[900]};
  text-transform: uppercase;
`

const SpecificationWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[10]};

  ${({ theme }) =>
    createMediaStyles(() => ({
      phone: `padding: 0 calc(${theme.grid.containerGutters} / 2);`,
      tablet: `padding-left: ${theme.spacing[1]}; padding-right: ${theme.spacing[0]};`
    }))}
`

const PropertyList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`

const PropertyItem = styled.li`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`

const PropertyText = styled.span`
  ${({ theme }) => theme.textStyles.p2};
  color: ${({ theme }) => theme.palette.grey[700]};
`

const Value = styled.p`
  margin: 0;
  ${({ theme }) => theme.textStyles.p2};
  color: ${({ theme }) => theme.palette.grey[900]};
`

const EquipmentPrice = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[4]}`};
  color: ${({ theme }) => theme.palette.grey[900]};
  ${({ theme }) => theme.textStyles.p1};
`

const EquipmentPriceTitleWrapper = styled.div`
  position: relative;
  flex-grow: 1;
  margin: 0;
  padding-right: ${({ theme }) => theme.spacing[1]};

  &:before {
    content: '';
    position: absolute;
    bottom: 7px;
    left: 0;
    z-index: -1;
    display: block;
    width: 100%;
    border-bottom: 1px black dotted;
  }
`

const EquipmentPriceTitle = styled.span`
  margin: 0;
`

const EquipmentPriceValue = styled.p`
  margin: 0;
`
